// §7.7 / D-042 — what *this* browser can actually do, probed once at boot.
//
// D-042 is "Chromium-first, everywhere-degrades", and the honest version of that is a record the
// UI reads rather than a `navigator.userAgent` test scattered through it. §11.6's About page
// states these facts to the user; §7.7's drag-out reads `dragOut` to decide whether a drag handle
// would do anything at all.

export interface BrowserCapabilities {
  /** §7.7 — a drag out of Hangar can carry a file to the host OS (`DownloadURL`). */
  dragOut: boolean;
  /** A native open picker exists. Used only for host-OS import, never over the VFS (§7.7). */
  nativeOpenPicker: boolean;
  nativeSavePicker: boolean;
  /** §7.2 — `createSyncAccessHandle` in a worker, the fast write path. */
  syncAccessHandle: boolean;
  /** §7.2 — `FileSystemFileHandle.move()`, without which a write cannot end in a rename (D-064). */
  fileHandleMove: boolean;
  /** Whether a directory can be walked out of a drop (§7.7's `webkitGetAsEntry`). */
  directoryImport: boolean;
}

/** The narrow shape of the global this probe reads. Injected, so the unit tests can lie to it. */
export interface CapabilityScope {
  showOpenFilePicker?: unknown;
  showSaveFilePicker?: unknown;
  DataTransferItem?: { prototype?: object } | undefined;
  FileSystemFileHandle?: { prototype?: object } | undefined;
  FileSystemFileHandle_move?: never;
}

function hasPrototypeMember(ctor: { prototype?: object } | undefined, member: string): boolean {
  const proto = ctor?.prototype;
  return typeof proto === 'object' && proto !== null && member in proto;
}

/**
 * `DownloadURL` has no feature test of its own — it is a Chromium DataTransfer convention, and
 * `setData` accepts any string as a type, so writing one and reading it back proves nothing.
 * It ships with the File System Access API and is absent wherever that is, so the save picker is
 * the honest proxy; M5.7's verify names exactly that correspondence.
 */
export function probeCapabilities(scope: CapabilityScope): BrowserCapabilities {
  const nativeSavePicker = typeof scope.showSaveFilePicker === 'function';
  return {
    dragOut: nativeSavePicker,
    nativeOpenPicker: typeof scope.showOpenFilePicker === 'function',
    nativeSavePicker,
    syncAccessHandle: hasPrototypeMember(scope.FileSystemFileHandle, 'createSyncAccessHandle'),
    fileHandleMove: hasPrototypeMember(scope.FileSystemFileHandle, 'move'),
    directoryImport: hasPrototypeMember(scope.DataTransferItem, 'webkitGetAsEntry'),
  };
}

/** The record for the running browser, computed once. */
export function browserCapabilities(): BrowserCapabilities {
  return probeCapabilities(globalThis as unknown as CapabilityScope);
}
